import {ChartDataPoint, ChartSeries} from "../types/chart";
import {DataTransformOptions, ChartDataProcessor, ValidationResult} from "../types/chartUtils";

export const createChartDataProcessor = (): ChartDataProcessor => {
  const transform = (data: ChartDataPoint[], options: DataTransformOptions = {}): ChartDataPoint[] => {
    let processedData = [...data];

    if (options.sortBy) {
      processedData = sortData(processedData, options.sortBy);
    }

    if (options.filterOutliers) {
      processedData = filterOutliers(processedData, 2);
    }

    if (options.smoothing?.enabled) {
      processedData = smooth(processedData, options.smoothing.window, options.smoothing.method);
    }

    if (options.aggregation?.enabled) {
      processedData = aggregate(processedData, options.aggregation.interval, options.aggregation.method);
    }

    return processedData;
  };

  const aggregate = (data: ChartDataPoint[], interval: number, method: string): ChartDataPoint[] => {
    if (data.length === 0) {
      return [];
    }

    const grouped = new Map<number, ChartDataPoint[]>();

    for (const point of data) {
      const bucketKey = Math.floor(point.timestamp / interval) * interval;
      if (!grouped.has(bucketKey)) {
        grouped.set(bucketKey, []);
      }
      grouped.get(bucketKey)!.push(point);
    }

    const aggregated: ChartDataPoint[] = [];

    Array.from(grouped.entries()).forEach(([timestamp, points]) => {
      let value: number;

      switch (method) {
        case "avg":
          value = points.reduce((sum: number, p: ChartDataPoint) => sum + p.value, 0) / points.length;
          break;
        case "sum":
          value = points.reduce((sum: number, p: ChartDataPoint) => sum + p.value, 0);
          break;
        case "min":
          value = Math.min(...points.map((p: ChartDataPoint) => p.value));
          break;
        case "max":
          value = Math.max(...points.map((p: ChartDataPoint) => p.value));
          break;
        case "count":
          value = points.length;
          break;
        default:
          value = points.reduce((sum: number, p: ChartDataPoint) => sum + p.value, 0) / points.length;
      }

      aggregated.push({
        timestamp,
        value,
        metadata: {
          aggregatedCount: points.length,
          method,
          interval
        }
      });
    });

    return aggregated.sort((a, b) => a.timestamp - b.timestamp);
  };

  const smooth = (data: ChartDataPoint[], window: number, method: string): ChartDataPoint[] => {
    if (data.length === 0 || window <= 1) {
      return data;
    }

    const smoothed: ChartDataPoint[] = [];

    for (let i = 0; i < data.length; i++) {
      const start = Math.max(0, i - Math.floor(window / 2));
      const end = Math.min(data.length, i + Math.ceil(window / 2));
      const windowData = data.slice(start, end);

      let smoothedValue: number;

      if (method === "exponential" && i > 0) {
        const alpha = 2 / (window + 1);
        smoothedValue = alpha * data[i].value + (1 - alpha) * smoothed[i - 1].value;
      } else {
        smoothedValue = windowData.reduce((sum, p) => sum + p.value, 0) / windowData.length;
      }

      smoothed.push({
        ...data[i],
        value: smoothedValue,
        metadata: {
          ...data[i].metadata,
          smoothed: true,
          window,
          method
        }
      });
    }

    return smoothed;
  };

  const filterOutliers = (data: ChartDataPoint[], threshold: number = 2): ChartDataPoint[] => {
    if (data.length < 3) {
      return data;
    }

    const values = data.map((d) => d.value);
    const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
    const stdDev = Math.sqrt(values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length);

    return data.filter((point) => {
      const zScore = Math.abs((point.value - mean) / stdDev);
      return zScore <= threshold;
    });
  };

  const sortData = (data: ChartDataPoint[], sortBy: "timestamp" | "value"): ChartDataPoint[] => {
    return [...data].sort((a, b) => {
      if (sortBy === "timestamp") {
        return a.timestamp - b.timestamp;
      }
      return a.value - b.value;
    });
  };

  return {
    transform,
    aggregate,
    smooth,
    filterOutliers
  };
};

export const validateChartData = (data: ChartDataPoint[]): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!Array.isArray(data)) {
    errors.push("Data must be an array");
    return {isValid: false, errors, warnings};
  }

  if (data.length === 0) {
    warnings.push("Data array is empty");
  }

  for (let i = 0; i < data.length; i++) {
    const point = data[i];

    if (typeof point.timestamp !== "number" || isNaN(point.timestamp)) {
      errors.push(`Invalid timestamp at index ${i}: ${point.timestamp}`);
    }

    if (typeof point.value !== "number" || isNaN(point.value)) {
      errors.push(`Invalid value at index ${i}: ${point.value}`);
    }

    if (point.timestamp < 0) {
      warnings.push(`Negative timestamp at index ${i}: ${point.timestamp}`);
    }
  }

  if (data.length > 1) {
    const timestamps = data.map((d) => d.timestamp);
    const sortedTimestamps = [...timestamps].sort((a, b) => a - b);
    const isChronological = timestamps.every((t, i) => t === sortedTimestamps[i]);

    if (!isChronological) {
      warnings.push("Data is not in chronological order");
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

export const validateChartSeries = (series: ChartSeries): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!series.name || typeof series.name !== "string") {
    errors.push("Series must have a valid name");
  }

  if (!series.data || !Array.isArray(series.data)) {
    errors.push("Series must have a data array");
    return {isValid: false, errors, warnings};
  }

  const dataValidation = validateChartData(series.data);
  errors.push(...dataValidation.errors);
  warnings.push(...dataValidation.warnings);

  if (!["line", "bar", "area"].includes(series.type)) {
    errors.push(`Invalid series type: ${series.type}`);
  }

  if (series.color && !/^#[0-9A-F]{6}$/i.test(series.color)) {
    warnings.push(`Color format should be hex: ${series.color}`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};
